/**
 * buildNativePrintStylesheet — E0 SPIKE (export rewrite, 2026-09-06).
 *
 * The stylesheet the NATIVE Chromium print path injects instead of running
 * Paged.js. Chromium's own print engine fragments the continuous report; this
 * sheet supplies (1) the `@page` box (size + the shared margin token),
 * (2) running head / running foot / folio as `@page` MARGIN BOXES — probed on
 * the pinned Chrome-for-Testing 148 (scripts/spike/exportRewrite/
 * probeMarginBoxes.mjs): `counter(page)` / `counter(pages)` render and
 * `@page :first` suppresses them on the cover, which the Puppeteer template
 * path cannot do — and (3) the first-cut FRAGMENTATION CONTRACT the section
 * primitives must meet (measured, then hardened, in the spike).
 *
 * Scope discipline: every rule is scoped under `html[data-native-print]`, an
 * attribute only the native harness path stamps, so the deck, the viewer and
 * the DOCX/PPTX exports are byte-identical while this is measured.
 */
import {
  CSS_PX_PER_MM,
  REPORT_PAGE_MARGIN_CSS,
  REPORT_PAGE_MARGIN_MM,
  type ReportPageNumberFormat,
} from '../reportLayoutTokens';
import { demonstrationDisclosureV1Schema, type DemonstrationDisclosureV1 } from '../../types/demonstration';

export type NativePrintFurniture = 'css' | 'template' | 'none';

export const NATIVE_PAGE_DIMENSIONS_MM = {
  a4: { width: 210, height: 297 },
  letter: { width: 215.9, height: 279.4 },
} as const;

export interface NativePrintStylesheetInput {
  pageSize: 'a4' | 'letter';
  furniture: NativePrintFurniture;
  /** Running head text, e.g. "Redhead Parcel · Generation & Impact Report". */
  runningHead: string;
  /** Site label folded into the folio line ("Datacrate · <site> · Page N of M"). */
  siteName: string;
  brand?: string;
  pageNumberFormat: ReportPageNumberFormat;
  /** Literal font stack for the margin boxes (page context cannot read the
   *  report root's custom properties). */
  fontFamily: string;
  /** Resolved paper tint; painted on the root canvas so it reaches the sheet. */
  pageBackground: string;
  inkColor?: string;
  /** Public-safe frozen notice; never a live site/theme setting. */
  demonstrationDisclosure?: DemonstrationDisclosureV1;
}

/** Printable width in CSS px for the page size (96 px/in, Chromium print). */
export function nativePrintableWidthPx(pageSize: 'a4' | 'letter'): number {
  const { width } = NATIVE_PAGE_DIMENSIONS_MM[pageSize];
  return Math.round((width - REPORT_PAGE_MARGIN_MM.left - REPORT_PAGE_MARGIN_MM.right) * CSS_PX_PER_MM);
}

/** A CSS string token safe for `content:` — quotes/backslashes stripped. */
function cssString(value: string): string {
  // Only these can leave a quoted CSS string or its HTML host: '"' and '\\' end
  // or escape the string, '<'/'>' could close the <style> element. '&', '{',
  // '}' and ';' are ordinary characters inside a string and real site names
  // carry them ("Generation & Impact"), so they stay.
  return `"${value.replace(/[\\"<>]/g, '').replace(/\s+/g, ' ').trim()}"`;
}

/** A page background is a colour or nothing — an unparsable value would end the @page block. */
function safeColor(value: string | undefined, fallback: string): string {
  const v = (value ?? '').trim();
  return /^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%]+\)|hsla?\([\d\s.,%]+\)|[a-z]{3,20})$/i.test(v) ? v : fallback;
}

function folioContent(input: NativePrintStylesheetInput): string {
  const brand = cssString(input.brand ?? 'Datacrate');
  const site = input.siteName.trim() ? cssString(`· ${input.siteName.trim()} ·`) : cssString('·');
  const page =
    input.pageNumberFormat === 'page-of-total'
      ? `"Page " counter(page) " of " counter(pages)`
      : input.pageNumberFormat === 'roman-front'
        ? 'counter(page)'
        : 'counter(page)';
  return `${brand} " " ${site} " " ${page}`;
}

export function buildNativePrintStylesheet(input: NativePrintStylesheetInput): string {
  const size = input.pageSize === 'letter' ? 'letter' : 'A4';
  const dims = NATIVE_PAGE_DIMENSIONS_MM[input.pageSize];
  const ink = safeColor(input.inkColor, '#6b7280');
  const pageBackground = safeColor(input.pageBackground, '#ffffff');
  const font = input.fontFamily.replace(/[;{}]/g, '');
  const furnitureFont = `font: 8.5pt/1.3 ${font}; color: ${ink}; letter-spacing: 0.01em;`;
  const printableWidth = `calc(${dims.width}mm - ${REPORT_PAGE_MARGIN_MM.left}mm - ${REPORT_PAGE_MARGIN_MM.right}mm)`;
  const disclosure = input.demonstrationDisclosure === undefined ? undefined : demonstrationDisclosureV1Schema.parse(input.demonstrationDisclosure);

  const marginBoxes =
    input.furniture === 'css'
      ? `@page {
  @top-center { content: ${cssString(input.runningHead)}; ${furnitureFont} vertical-align: bottom; padding-bottom: 3.5mm; }
  @bottom-center { content: ${folioContent(input)}; ${furnitureFont} vertical-align: top; padding-top: 4mm; }
}
@page :first {
  @top-center { content: none; }
  @bottom-center { content: none; }
}`
      : '';
  // The fixed notice uses the existing bottom margin, including page one.
  // Preserve the folio on body pages and reserve a separate second line.
  const demonstrationBoxes = disclosure ? `@page {
  @bottom-center { content: ${input.furniture === 'css' ? `${folioContent(input)} "\\A" ` : ''}${cssString(disclosure.notice)}; ${furnitureFont} white-space: pre-line; vertical-align: top; padding-top: 2mm; }
}
@page :first {
  @bottom-center { content: ${cssString(disclosure.notice)}; ${furnitureFont} white-space: pre-line; vertical-align: top; padding-top: 2mm; }
}` : '';

  return `/* native-print spike stylesheet — E0 */
@page { size: ${size}; margin: ${REPORT_PAGE_MARGIN_CSS}; }
${marginBoxes}${demonstrationBoxes ? `\n${demonstrationBoxes}` : ''}
/* The paper tint covers the WHOLE sheet (as the deck's .pagedjs_page did): the
   root canvas background propagates to the page box under printBackground. */
html[data-native-print] { background: ${pageBackground} !important; }
html[data-native-print] body { margin: 0; background: ${pageBackground} !important; }
/* The report lays out at the printable width on SCREEN too, so every JS-measured
   exhibit (recharts ResponsiveContainer, ResizeObserver cards) measures the exact
   column the print engine will use — no raster, no re-measure. */
html[data-native-print] [data-print-frame] { width: ${printableWidth}; margin: 0; }
/* The report stamps --report-printable-width inline as the A4 literal; on Letter
   the frame is wider and shorter, so re-stamp the page dimension tokens or every
   chart measures 688 on 710 and covers size min-height against 297mm (A4). */
html[data-native-print] [data-report-root] { --report-printable-width: ${printableWidth} !important; --report-page-height: ${dims.height}mm !important; --report-page-width: ${dims.width}mm !important; max-width: ${printableWidth} !important; }
html[data-native-print] [data-print-frame] [data-report-root] {
  box-shadow: none;
  overflow: visible;
}
/* DOCUMENT ORDER. The report root is a flex column and Chromium paginates the
   FLEX order, which Paged.js ignored. sectionStyle stamps an INLINE order on
   every planned section (cover -2, Contents -1, body 1..N, fallback 999), and
   inline styles beat this sheet, so those keep their authored positions. What
   has no inline order computes 0 and therefore floats ahead of the whole body:
   that is how "What this report does not know" printed straight after the
   Contents. Giving the root's children a DEFAULT of 500 puts anything
   unplanned after the planned run instead of before it. The chrome sections are
   then pinned by NAME at both ends, because they do not always carry an inline
   order themselves: without the cover pin the 500 default swept the cover into
   the middle of the document and the report opened on its executive summary.
   Neutralising order outright is not the fix either — that moved a standalone
   Contents behind the executive summary. These pins are stylesheet rules, so an
   authored inline order still wins wherever the product sets one. */
html[data-native-print] [data-report-root] > * { order: 500; }
html[data-native-print] [data-report-root] > [data-section="cover"] { order: -2; }
html[data-native-print] [data-report-root] > [data-section="toc"] { order: -1; }
html[data-native-print] [data-report-root] > [data-section="legalDisclaimer"] { order: 99997; }
html[data-native-print] [data-report-root] > [data-section="footer"] { order: 99998; }
html[data-native-print] [data-report-root] > [data-testid="report-closing-tail"] { order: 99999; }
/* An empty closing-tail wrapper (all its sections rendered elsewhere) must not
   occupy a page: index.css gives it break-before: page. */
html[data-native-print] [data-testid="report-closing-tail"]:not(:has([data-section])) { display: none; }
@media print {
  /* ── Fragmentation contract v9 (measured by the E0 oracle; v9 after the E2 review) ──
     RULE 0 — NO NESTED MULTICOL IN THE PRINT TREE. Chromium fragments a
     column container inside a page as a nested fragmentation context and
     moves whole sections to fresh pages around it; the E0 bisect
     (scripts/spike/exportRewrite, contract n4) showed every blank tail on the
     measured cells disappears when column properties are neutralised. The
     single-column presets need none; a two-column flow is a separate design
     decision for E2, never an incidental style. */
  html[data-native-print] [data-report-root] * { column-count: auto !important; columns: auto !important; column-width: auto !important; column-span: none !important; }
  /* RULE 1 — tables flow across pages when long (≥ 8 rows): rows stay whole,
     the head repeats; short tables stay whole. The pinned Chrome splits tables
     correctly (probeFragmentation A–F). */
  html[data-native-print] [data-report-root] table:has(tbody > tr:nth-child(8)) { break-inside: auto !important; page-break-inside: auto !important; }
  html[data-native-print] [data-report-root] table thead { display: table-header-group; }
  html[data-native-print] [data-report-root] table tr { break-inside: avoid; page-break-inside: avoid; }
  /* The Contents list never splits; the legal notice keeps whole when it fits. */
  /* Atoms. The generic "sections may break" rule below is written with :where()
     (zero specificity) so these win by default; v8 had it at (0,4,1) and the
     Important Notice printed split across a page boundary. */
  html[data-native-print] [data-report-root] > [data-section="toc"] { break-inside: avoid; }
  html[data-native-print] [data-report-root] > [data-section="legalDisclaimer"] { break-inside: avoid; break-before: auto; }
  /* Legal paragraphs never fragment: the deck kept the notice whole or added an
     explicit continuation heading, and the artifact validator proves each
     authored paragraph verbatim in the page text — a paragraph split across a
     page boundary has running furniture between its halves. */
  html[data-native-print] [data-report-root] > [data-section="legalDisclaimer"] p { break-inside: avoid; }
  /* Cover owns page 1: hard page break after it, never rely on min-height overflow. */
  html[data-native-print] [data-report-root] > [data-section="cover"] { break-after: page; }
  html[data-native-print] [data-report-root] > #cover,
  html[data-native-print] [data-report-root] > [data-section="cover"] { break-after: page !important; page-break-after: always !important; }
  html[data-native-print] [data-report-root] :is(.instrument-dial-caption, [data-export-capture] svg text[font-size="8"], [data-export-capture] svg text[font-size="9"], [data-export-capture] svg text[font-size="10"], [data-figure] svg text[font-size="8"], [data-figure] svg text[font-size="9"], [data-figure] svg text[font-size="10"], [data-export-capture] .recharts-legend-item-text) { font-size: max(8.25pt, 11px) !important; }
  /* Section openers (eyebrow + h2 + standfirst + rule) are one atom that keeps with
     the first body block. */
  html[data-native-print] [data-layout-group="opener"] { break-inside: avoid; break-after: avoid; }
  html[data-native-print] [data-layout-group="opener"] + * { break-before: avoid; }
  /* Head–body pairs never split. */
  html[data-native-print] [data-layout-group="head-body"] { break-inside: avoid; }
  /* RULE 3 (contract v9) — atomicity lives on LEAVES only, so it composes across
     nesting. A row GROUP that contains other groups or rows is a container and
     flows; a leaf group with five or more children flows and each child is the
     atom; a small leaf group stays whole. v8 put avoid on any group with < 5
     children, which made a Key Findings category (h4 + ul) atomic no matter how
     many findings its inner list held, and re-atomised a datasheet whose root
     was the single child of a wrapper group. Nothing binds a label to its first
     row (v6 opened a 57 % tail that way). index.css's [data-report-block] avoid
     yields to the same leaf rule when the block holds layout groups. */
  html[data-native-print] [data-layout-group="row-group"]:has([data-layout-group="row-group"], [data-layout-group="row"]) { break-inside: auto; }
  html[data-native-print] [data-report-block]:has([data-layout-group]) { break-inside: auto; }
  html[data-native-print] [data-layout-group="row-group"]:not(:has([data-layout-group])):has(> :nth-child(5)) { break-inside: auto; }
  html[data-native-print] [data-layout-group="row-group"]:not(:has([data-layout-group])):not(:has(> :nth-child(5))) { break-inside: avoid; }
  html[data-native-print] [data-layout-group="row-group"] > *:not(:has([data-layout-group])),
  html[data-native-print] [data-layout-group="row"] { break-inside: avoid; }
  /* TRAILING SPACE MAY NOT MAKE A PAGE. The document's last box carries padding
     and margin that Chromium will happily push onto a fresh sheet, producing a
     final page with nothing on it but the running head and folio (measured on
     interconnection: 11 pages, page 11 empty). The canonical appendix is the
     usual culprit because it is injected with its own padding. */
  /* Include the export marker so these terminal rules outrank index.css's
     [data-report-root][data-print-export] [data-section]:not([data-section="cover"])
     padding. Otherwise its 10px bottom padding survives on both the appendix
     and its last item and can fragment into an empty final financial page. */
  html[data-native-print] [data-print-frame],
  html[data-native-print] [data-print-frame] :has(> [data-report-root]) {
    min-height: 0 !important;
    padding-bottom: 0 !important;
    margin-bottom: 0 !important;
  }
  html[data-native-print] [data-report-root] [data-admin-appendix] { display: none !important; }
  html[data-native-print] [data-report-root] #cover:has([data-testid="cover-demonstration-notice"]) [data-cover-variant] {
    min-height: calc(var(--report-page-height, 297mm) - 14mm - 16mm - 1mm - 140px) !important;
  }
  html[data-native-print] [data-report-root][data-print-export] > :last-child { padding-bottom: 0 !important; margin-bottom: 0 !important; }
  html[data-native-print] [data-report-root][data-print-export] [data-canonical-semantic-appendix="true"] { padding-bottom: 0 !important; margin-bottom: 0 !important; }
  html[data-native-print] [data-report-root][data-print-export] [data-canonical-semantic-appendix="true"] [data-canonical-item] { padding-top: 1px !important; padding-bottom: 1px !important; }
  html[data-native-print] [data-report-root][data-print-export] [data-canonical-semantic-appendix="true"] > :last-child { margin-bottom: 0 !important; padding-bottom: 0 !important; }
  /* Exhibits are atoms. */
  html[data-native-print] .recharts-responsive-container,
  html[data-native-print] .recharts-wrapper,
  html[data-native-print] [data-testid="chart-frame"],
  html[data-native-print] [data-export-capture],
  html[data-native-print] figure { break-inside: avoid; }
  /* Sections themselves flow (a long section legitimately spans pages) — at zero
     specificity so the atoms above (Contents, legal notice, cover) win. */
  :where(html[data-native-print] [data-report-root] [data-section]) { break-inside: auto; }
  /* A leaf section with only an omission notice is a bounded heading/notice
     pair. Chromium does not carry keep-with-next through the nested empty-state
     shell when the section is a flex item (Pineywoods p6/p10). Keep that small
     pair together, without making populated or parent sections atomic. */
  html[data-native-print] [data-report-root] [data-section]:not(:has([data-section])):has([data-omit-reason="insufficient-data"]) { break-inside: avoid; page-break-inside: avoid; }
  /* RULE 2 (contract v5) — keep-with-next binds HEADINGS, never a section's whole
     first child. index.css binds '[data-section] > :first-child' to its next
     sibling; when that child is a body wrapper (Key Findings: a 282 px row
     group) Chromium moves the entire section to the next page to honour it —
     the last two native blank tails (feasibility house/monograph A4, 67 %). */
  html[data-native-print] [data-report-root] [data-section]:not([data-section="cover"]) > :first-child:not(h1):not(h2):not(h3):not(h4):not([data-heading]):not([data-layout-group="opener"]) { break-after: auto !important; page-break-after: auto !important; }
  /* Headings keep with next; Chromium honours break-after:avoid in block
     fragmentation — the oracle's heading-at-foot metric verifies it. */
  html[data-native-print] [data-report-root] h2,
  html[data-native-print] [data-report-root] h3,
  html[data-native-print] [data-report-root] h4,
  html[data-native-print] [data-report-root] [data-heading] { break-after: avoid; break-inside: avoid; }
  html[data-native-print] [data-report-root] li { break-inside: avoid; }
  /* Bracketed citations participate in the native line box. Zero-height raised
     glyphs can paint below a fragmented page even when their line moves to the
     next page. Keep the hyperlink and smaller text on the prose baseline. */
  html[data-native-print] [data-report-root] a[data-ref],
  html[data-native-print] [data-report-root] a[data-cite-ref] { display: inline; line-height: normal; vertical-align: baseline; }
  /* Captions keep with the exhibit they name (below-table captions opened the
     next page alone in E0). Native-only hook — see EditorialFigureCaption. */
  html[data-native-print] [data-native-figure-caption] { break-before: avoid; break-inside: avoid; }
  /* Hand-built exhibits (div stacks) are atoms per ROW, never per stack. */
  html[data-native-print] [data-testid="lcoe-breakdown-cost-stack"] > * { break-inside: avoid; }

  html[data-native-print] [data-print-frame],
  html[data-native-print] [data-print-frame] [data-report-root] {
    width: ${printableWidth} !important;
    max-width: 100% !important;
    overflow-x: clip !important;
    box-sizing: border-box !important;
  }

  html[data-native-print] [data-report-root] [data-section]:not([data-section="cover"]) {
    width: 100% !important;
    max-width: 100% !important;
    overflow-x: clip !important;
    box-sizing: border-box !important;
  }

  html[data-native-print] [data-report-root] [data-section]:not([data-section="cover"]) :is(a, p, li, td, th, dd, dt, span, div, code, pre) {
    overflow-wrap: anywhere;
    word-break: break-word;
    min-width: 0;
  }

  html[data-native-print] [data-report-root]:not([data-summary-version]) [data-section]:not([data-section="cover"]) table {
    width: 100% !important;
    max-width: 100% !important;
    table-layout: fixed;
  }

  html[data-native-print] [data-report-root] :is(.overflow-x-auto, .overflow-y-auto, .overflow-auto, .overflow-scroll) {
    overflow: visible !important;
    overflow-x: visible !important;
    overflow-y: visible !important;
  }

  html[data-native-print] [data-report-root] [data-section]:not([data-section="cover"]) :is(.lg\:grid-cols-2, .lg\:grid-cols-5, .md\:grid-cols-2, .sm\:grid-cols-2) {
    grid-template-columns: minmax(0, 1fr) !important;
    break-inside: auto !important;
    page-break-inside: auto !important;
  }

  html[data-native-print] [data-report-root][data-editorial] [data-editorial-lead]::first-letter {
    font-size: 2.5em !important;
    line-height: 0.92 !important;
    padding-top: 0.08em !important;
  }

  /* Prevent multi-item flex/block wrappers from forcing premature page breaks and blank tails */
  html[data-native-print] [data-report-root] [data-span-columns="true"],
  html[data-native-print] [data-report-root] [data-layout-group="head-body"],
  html[data-native-print] [data-report-root] [data-testid="finding-claims-list"],
  html[data-native-print] [data-report-root] [data-testid="contextual-key-findings"],
  html[data-native-print] [data-report-root] [data-findings-group="true"],
  html[data-native-print] [data-report-root] [data-testid="project-intent-overview-panel"],
  html[data-native-print] [data-report-root] [data-testid="project-intent-overview-panel"] > div,
  html[data-native-print] [data-report-root] [data-testid="screening-mode-scorecard"] {
    display: block !important;
    break-inside: auto !important;
    page-break-inside: auto !important;
  }
  html[data-native-print] [data-report-root] [data-testid="screening-mode-scorecard"] table,
  html[data-native-print] [data-report-root] [data-testid="water-screening-table"],
  html[data-native-print] [data-report-root] [data-testid="conclusion-category-status-table"],
  html[data-native-print] [data-report-root] [data-layout-group="row-group"]:has([data-table-splittable="true"]),
  html[data-native-print] [data-report-root] [data-table-splittable="true"],
  html[data-native-print] [data-report-root] [data-testid="water-screening-view"] [data-layout-group="callout"],
  html[data-native-print] [data-report-root] [data-layout-group="callout"]:has(li:nth-child(4)) {
    break-inside: auto !important;
    page-break-inside: auto !important;
  }
  html[data-native-print] [data-report-root] [data-testid="screening-mode-scorecard"] > :first-child,
  html[data-native-print] [data-report-root] [data-layout-group="callout"]:has(li:nth-child(4)) > :first-child {
    break-after: avoid !important;
    page-break-after: avoid !important;
  }
  html[data-native-print] [data-report-root] [data-testid="property-map-block"],
  html[data-native-print] [data-report-root] [data-testid="project-intent-spec-rail"] {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }
  html[data-native-print] [data-report-root] [data-testid="project-intent-spec-rail"] > :first-child,
  html[data-native-print] [data-report-root] [data-testid="feasibility-profile-verdict-rail"] {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
    break-after: avoid !important;
    page-break-after: avoid !important;
  }
}
`;
}
